import React, { useState, useEffect, useContext } from 'react';
import WIOwnership from 'src/models/tract_detail/WIOwnership';
import CardContainer from 'src/components/common/CardContainer.redesign';
import Owner from 'src/models/tract_detail/Owner';
import Button from 'src/components/controls/forms/redesign/FormButton';
import Dialog from 'src/components/common/Dialog';
import DialogList from 'src/dialogs';
import { UseFormMethods } from 'react-hook-form';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import LeaseholdOwnership from 'src/models/leasehold/LeaseholdOwnership';
import WIOwnershipCard from '../../TractComponents/WIOwnershipCard.redesign';
import WIContractOwnership from 'src/models/tract_detail/WIContractOwnership';
import MathUtil from 'src/util/MathUtil';

interface IContractWorkingInterestListProps extends Partial<Pick<UseFormMethods, 'register' | 'errors' | 'getValues' | 'watch' | 'setValue'>> {
    owners: Array<Owner>;
    wiContractOwnerships: Array<WIContractOwnership>;
    isGovStateBlm: boolean;
    royalty: number;
    contractType?: string;
    prefix?: string;

    setDialogShowing: (dialogName: string) => void;
    tryAddWI: () => void;
    onAddORRI: (parentOwnershipId: number) => void;
    onCopyORRI: (ownershipId: number) => void;
    onDeleteORRI: (ownershipId: number) => void;
    deleteContractOwnership: (contractOwnershipId: number) => void;
    openOwnershipNotes: (ownershipId: number) => void;
}

const ContractWorkingInterestList = (props: IContractWorkingInterestListProps) => {
    const [contractOwnershipId, setContractOwnershipId] = useState(0);
    const [wiTotal, setWITotal] = useState(0);
    const appContext = useContext<IAppContextState>(AppContext);
    const [isNewHold, setIsNewHold] = useState(false);

    const onDeleteContractOwnership = (id: number) => {
        setContractOwnershipId(id);
        props.setDialogShowing(DialogList.CONFIRM_CONTRACT_OWNERSHIP_DELETE);
    };

    const handleWIInterestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const values = props.getValues();
        const wiOwnerships: Array<WIOwnership> = values?.[props.prefix].contractOwnerships;
        if (wiOwnerships) {
            let total = 0;
            wiOwnerships.forEach((wi) => {
                total += parseFloat(wi.decimalInterest + '') || 0;
            });
            setWITotal(total);
        }
        const royalty = parseFloat(values?.leasehold?.royalty + '') || 0;

        (wiOwnerships as unknown as LeaseholdOwnership[])?.forEach((lo: LeaseholdOwnership, i: number) => {
            const orriBurden = lo?.orriOwnerships?.reduce((acc: number, curr: WIOwnership) => acc + (parseFloat(curr.decimalInterest + '') || 0), 0);
            props.setValue(`${props.prefix}.contractOwnerships[${i}].orriInterest`, MathUtil.cleanNumber((orriBurden || 0)));
            props.setValue(
                `${props.prefix}.contractOwnerships[${i}].nriInterest`,
                MathUtil.cleanNumber(((1 - royalty) * lo?.decimalInterest || 0) - ((orriBurden || 0) * lo?.decimalInterest || 0))
            );
        });
    };

    // TODO: really hacky and questionable, Need to do deep dive on watch and figure out best practices for this. (watch in parent and sub compoments)
    useEffect(() => {
        if (props.wiContractOwnerships?.length > 0) {
            setWITotal(Number(MathUtil.cleanNumber(props.wiContractOwnerships.reduce((acc, curr) => acc + curr.decimalInterest, 0))));

            props.wiContractOwnerships?.forEach((lo: WIContractOwnership, i: number) => {
                const orriBurden = lo?.orriOwnerships?.reduce(
                    (acc: number, curr: WIContractOwnership) => acc + (parseFloat(curr.decimalInterest + '') || 0),
                    0
                );
                // TODO: hacky, not sure why this is needed.
                setTimeout(() => {
                    props.setValue(`${props.prefix}.contractOwnerships[${i}].orriInterest`, MathUtil.cleanNumber(orriBurden || 0));
                    props.setValue(
                        `${props.prefix}.contractOwnerships[${i}].nriInterest`,
                        MathUtil.cleanNumber(((1 - props.royalty) * lo?.decimalInterest || 0) - ((orriBurden || 0) * lo?.decimalInterest || 0))
                    );
                }, 0);
            });
        } else {
            setWITotal(0);
        }
    }, [props.wiContractOwnerships, props.royalty]);

    // Maybe there is a better way to account for different contract types
    useEffect(() => {
        if (appContext.surfaceWaiverId === 0 || appContext.solarLeaseId === 0 || appContext.batteryStorageId === 0) {
            setIsNewHold(true);
        } else {
            setIsNewHold(false);
        }
    }, [appContext.surfaceWaiverId, appContext.solarLeaseId, appContext.batteryStorageId]);

    const card = (wi: WIContractOwnership, index: number) => {
        return (
            <div key={index} style={{ width: '100%', margin: 0 }}>
                <WIOwnershipCard
                    ownership={wi}
                    owner={props.owners.find((o) => o.ownerLookUpId === wi.ownerLookUpId)}
                    index={index}
                    owners={props.owners}
                    addORRI={props.onAddORRI}
                    copyORRI={props.onCopyORRI}
                    deleteORRI={props.onDeleteORRI}
                    deleteLeaseholdOwnership={onDeleteContractOwnership}
                    showDialog={props.setDialogShowing}
                    openOwnershipNotes={props.openOwnershipNotes}
                    register={props.register}
                    errors={props.errors}
                    onWIInterestChange={handleWIInterestChange}
                    namePrefix={`${props.prefix}.contractOwnerships`}
                />
            </div>
        );
    };

    const interestStatus = () => {
        const wi = parseFloat(wiTotal + '');
        if (wi < 1) {
            return 'low';
        }
        if (wi === 1) {
            return 'good';
        }
        if (wi > 1) {
            return 'high';
        }
        return 'good';
    };

    return (
        <>
            <div style={{ width: '100%' }}>
                <div className="wi-info ownership-list">
                    <div className={`totals interest-status-${interestStatus()} | RI ${props?.getValues('leasehold.royalty')}`}>
                        <div className="mr-1">{props.isGovStateBlm ? 'Total Operating Rights Interest' : 'Total Working Interest'}</div>
                        <div>
                            <span>{MathUtil.cleanNumber(wiTotal)}</span>
                            {/* <button onClick={calcWITotal}>Click me</button> */}
                        </div>
                    </div>
                    <div style={{ marginTop: '1rem' }}>
                        <Button
                            text={props.isGovStateBlm ? 'Add OR' : 'Add WI'}
                            size="medium"
                            intent="primary"
                            onClick={() => props.tryAddWI()}
                            disabled={isNewHold}
                        />
                    </div>
                </div>
            </div>
            {props.owners && props.wiContractOwnerships && (
                <CardContainer style={{ width: '100%' }}>{props.wiContractOwnerships.map(card)}</CardContainer>
            )}

            <Dialog
                title={props.isGovStateBlm ? 'Confirm Delete Operating Rights Interest' : 'Confirm Delete Working Interest'}
                dialogName={DialogList.CONFIRM_CONTRACT_OWNERSHIP_DELETE}
            >
                <h3>Are you sure want to permanently delete this {props.isGovStateBlm ? 'OR' : 'WI'} record?</h3>
                <Button text="Confirm Delete" size="medium" intent="primary" onClick={() => props.deleteContractOwnership(contractOwnershipId)} />
            </Dialog>
        </>
    );
};

export default ContractWorkingInterestList;



// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/redesign/ContractWorkingInterestList.tsx